import { forwardRef, useMemo, useState } from 'react'
import { MessageCustomFormProps } from './types'
import * as yup from 'yup'
import { captureError } from 'utils/sentry'
import { Controller, useForm } from 'react-hook-form'
import { twJoin } from 'tailwind-merge'
import { capitalize } from 'lodash'
import { useYupValidationResolver } from 'hooks/use-yup-validation-resolver'
import { MessageComponent } from './message-component'
import { createEvent, restore } from 'effector'
import { resetMessageForm } from './message-form.events'
import { useUnit } from 'effector-react'

const doSubmitValues = createEvent<Record<string, string> | null>()
const $submittedValues = restore(doSubmitValues, null).reset(resetMessageForm)

export const MessageCustomForm = forwardRef<
  HTMLDivElement,
  MessageCustomFormProps
>(({ onSubmit, type, t, formSchema }, ref) => {
  const { header, description, components } = formSchema

  const data = useUnit($submittedValues)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // TODO: needed type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmitForm = async (data: any) => {
    setIsSubmitting(true)

    let isCandidateFailed = false
    const errors: string[] = []
    components?.forEach(({ id, data_type, validations, label }) => {
      validations?.forEach(({ equals }) => {
        if (data_type === 'string') {
          if (
            (Array.isArray(equals) && equals.indexOf(data[id]) === -1) ||
            (typeof equals === 'string' && equals !== data[id])
          ) {
            isCandidateFailed = true
            errors.push(`${label}: ${data[id]}`)
          }
        }
      })
    })

    await onSubmit(data, isCandidateFailed, errors.join('\n'))
    doSubmitValues(data)
    setIsSubmitting(false)
  }

  const validationSchema = useMemo(() => {
    // TODO: needed type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shape: Record<string, any> = {}

    formSchema.components?.forEach(({ id, data_type, ui_validations }) => {
      const key = id

      if (data_type === 'string') {
        shape[key] = yup.string()
      }

      ui_validations?.forEach(({ required, error_message }) => {
        // eslint-disable-next-line no-prototype-builtins
        if (!shape.hasOwnProperty(key)) {
          captureError('No validation schema found for key', {
            form_schema: JSON.stringify(formSchema)
          })
          return
        }

        if (required) {
          shape[key] = shape[key].required(error_message)
        }
      })
    })

    return yup.object().shape(shape)
  }, [formSchema])

  const resolver = useYupValidationResolver(validationSchema)

  const {
    control,
    handleSubmit,
    formState: { errors }
    // TODO: needed type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useForm<any>({
    resolver,
    mode: 'onChange'
  })

  const errorsMessages = useMemo(
    () => [...Object.values(errors).map((error) => error!.message)],
    [errors]
  )

  const showErrors = useMemo(() => {
    return errorsMessages.length > 0
  }, [errorsMessages])

  if (!data) {
    return (
      <MessageComponent
        text={`The form was submitted successfully. Thank you!`}
        type={type}
        ref={ref}
      />
    )
  }

  return (
    <div
      ref={ref}
      style={{
        boxShadow: `
            0px 2px 0px 0px #E6E1EF
          `
      }}
      className="mx-auto mt-[10px] max-w-md rounded-[14px] border border-[#E0E0E0] bg-white p-6"
    >
      <h2 className="mb-[16px] text-left text-[17px] font-[500] text-gray-800">
        {header}
      </h2>
      {description && (
        <p className="mb-6 text-left text-[15px] text-gray-600">
          {description}
        </p>
      )}
      <form autoComplete="on" onSubmit={handleSubmit(onSubmitForm)}>
        <div className="gap-[16px] text-left">
          {components?.map(({ id, label, select_values, type }) => {
            if (type === 'select_buttons') {
              return (
                <Controller
                  key={id}
                  name={id}
                  control={control}
                  render={({ field }) => {
                    return (
                      <div>
                        {label && (
                          <div className="mb-[8px] text-[13px] font-[500]">
                            {label}
                          </div>
                        )}
                        <div
                          className="mb-[16px] w-fit rounded-[8px]"
                          style={{
                            boxShadow: '0px 1px 2px 0px #1018280D'
                          }}
                        >
                          {select_values?.map((item, index) => {
                            return (
                              <button
                                key={item}
                                type="button"
                                className={twJoin(
                                  'py-[10px] px-[16px] border-[1px] hover:bg-[#EDEDED] transition-colors duration-200 ease-in-out',
                                  index === 0 &&
                                    'rounded-tl-[8px] rounded-bl-[8px]',
                                  index === select_values.length - 1 &&
                                    'rounded-tr-[8px] rounded-br-[8px]',
                                  item === field.value && 'bg-[#EDEDED]'
                                )}
                                onClick={() => {
                                  field.onChange(item)
                                }}
                              >
                                {item}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )
                  }}
                />
              )
            }

            captureError('Unsupported input type in custom form', {
              type,
              form_schema: JSON.stringify(formSchema)
            })
            return <div key={id}>Unsupported input type: {type}</div>
          })}
        </div>
        <div className="flex flex-row items-center gap-4">
          <button
            type="submit"
            disabled={isSubmitting || showErrors}
            className="flex min-w-[120px] items-center justify-center rounded-xl border border-gray-300 bg-white px-[12px] py-[8px] font-semibold text-gray-800 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="-ml-1 mr-3 size-5 animate-spin text-gray-800"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t('welcome.buttons.submit.loading')}
              </>
            ) : (
              t('welcome.buttons.submit.text')
            )}
          </button>
          {showErrors && (
            <p className="text-left text-gray-500">
              {capitalize(errorsMessages![0] as string)}
            </p>
          )}
        </div>
      </form>
    </div>
  )
})

MessageCustomForm.displayName = 'MessageCustomForm'
